import _ from 'lodash';

import { DOCUMENT_INTAKE_ACTION_TYPE } from 'constants/documentIntake';

const initialState = {
  jobsById: {},
  jobIdsByLoan: {},
  activeJobIdByLoan: {},
  cancellingJobIds: {},
  creatingByLoan: {},
};

const upsertJob = (state, job) => ({
  ...state,
  jobsById: {
    ...state.jobsById,
    [job.id]: {
      ...(state.jobsById[job.id] ?? {}),
      ...job,
    },
  },
});

const handleCreateJob = (state, { loanApplicationId }) => ({
  ...state,
  creatingByLoan: { ...state.creatingByLoan, [loanApplicationId]: true },
});

const handleCreateJobSuccess = (state, { job, loanApplicationId, uploadLabel }) => {
  // Merge the uploadLabel into the job before upserting so the status card can
  // display "Processing <label>" without a separate store field. The label is
  // FE-only (not returned by the backend) and survives only for this session —
  // on hard-reload the card falls back to the backend's per-segment sourceFileName.
  const jobWithLabel = uploadLabel ? { ...job, uploadLabel } : job;
  const next = upsertJob(state, jobWithLabel);
  const existingIds = next.jobIdsByLoan[loanApplicationId] ?? [];

  return {
    ...next,
    creatingByLoan: _.omit(state.creatingByLoan, loanApplicationId),
    jobIdsByLoan: {
      ...next.jobIdsByLoan,
      [loanApplicationId]: existingIds.includes(job.id) ? existingIds : [job.id, ...existingIds],
    },
  };
};

const handleCreateJobFailure = (state, { loanApplicationId }) => ({
  ...state,
  creatingByLoan: _.omit(state.creatingByLoan, loanApplicationId),
});

const handleLoadJobSuccess = (state, { job }) => upsertJob(state, job);

const handleLoadJobsForLoanSuccess = (state, { loanApplicationId, jobs }) => {
  const ids = jobs.map(j => j.id);
  const nextJobsById = jobs.reduce(
    (acc, j) => ({ ...acc, [j.id]: { ...(acc[j.id] ?? {}), ...j } }),
    state.jobsById,
  );

  return {
    ...state,
    jobsById: nextJobsById,
    jobIdsByLoan: { ...state.jobIdsByLoan, [loanApplicationId]: ids },
  };
};

const handleCancelJob = (state, { id }) => ({
  ...state,
  cancellingJobIds: { ...state.cancellingJobIds, [id]: true },
});

const handleCancelJobSettled = (state, { id }) => ({
  ...state,
  cancellingJobIds: _.omit(state.cancellingJobIds, id),
});

const handleCancelJobSuccess = (state, { job }) => {
  const next = upsertJob(handleCancelJobSettled(state, { id: job.id }), job);

  return next;
};

const handleJobUpsert = (state, { job }) => {
  if (!job?.id) {
    return state;
  }

  return upsertJob(state, job);
};

const handleSetActiveJobForLoan = (state, { loanApplicationId, jobId }) => ({
  ...state,
  activeJobIdByLoan: { ...state.activeJobIdByLoan, [loanApplicationId]: jobId },
});

const handleClearActiveJobForLoan = (state, { loanApplicationId }) => ({
  ...state,
  activeJobIdByLoan: _.omit(state.activeJobIdByLoan, loanApplicationId),
});

const handlersByType = {
  [DOCUMENT_INTAKE_ACTION_TYPE.CREATE_JOB]: handleCreateJob,
  [DOCUMENT_INTAKE_ACTION_TYPE.CREATE_JOB_SUCCESS]: handleCreateJobSuccess,
  [DOCUMENT_INTAKE_ACTION_TYPE.CREATE_JOB_FAILURE]: handleCreateJobFailure,
  [DOCUMENT_INTAKE_ACTION_TYPE.LOAD_JOB_SUCCESS]: handleLoadJobSuccess,
  [DOCUMENT_INTAKE_ACTION_TYPE.LOAD_JOBS_FOR_LOAN_SUCCESS]: handleLoadJobsForLoanSuccess,
  [DOCUMENT_INTAKE_ACTION_TYPE.CANCEL_JOB]: handleCancelJob,
  [DOCUMENT_INTAKE_ACTION_TYPE.CANCEL_JOB_SUCCESS]: handleCancelJobSuccess,
  [DOCUMENT_INTAKE_ACTION_TYPE.CANCEL_JOB_FAILURE]: handleCancelJobSettled,
  [DOCUMENT_INTAKE_ACTION_TYPE.JOB_UPSERT]: handleJobUpsert,
  [DOCUMENT_INTAKE_ACTION_TYPE.SET_ACTIVE_JOB_FOR_LOAN]: handleSetActiveJobForLoan,
  [DOCUMENT_INTAKE_ACTION_TYPE.CLEAR_ACTIVE_JOB_FOR_LOAN]: handleClearActiveJobForLoan,
};

export const documentIntakeReducer = (state = initialState, action = {}) => {
  const handler = _.get(handlersByType, action.type);

  if (!handler) {
    return state;
  }

  return handler(state, action.payload);
};
